// #tag: ADMIN ACCESS CONTROL AND RBAC SCREENS
// #purpose: Visualizes and audits role definitions, permission matrix assignments, scope boundaries, and user associations.
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ChevronRight,
  KeyRound,
  Shield,
  Users,
} from 'lucide-react';
import { fetchAdminPermissions, fetchAdminPermission, fetchAdminRole, fetchAdminRoles } from '../../api/admin';
import {
  AdminEmptyState,
  AdminDetailCard,
  AdminKpiStrip,
  AdminPageLayout,
  AdminSearchInput,
  AdminStatusBadge,
  AdminTablePanel,
} from '../../components/admin/AdminPageLayout';
import { adminDetailIllustration, adminRoleIllustration } from '../../config/adminIllustrations';
import { LoadingState, ErrorState } from '../../components/states/StateViews';
import type { AdminRoleRow } from '../../types/admin';

function RoleBadge({ role }: { role: AdminRoleRow | string; label?: string }) {
  const id = typeof role === 'string' ? role : role.id;
  const text = typeof role === 'string' ? role : role.label;
  return (
    <span className={`wfx-admin-role-badge role-${id}`}>
      {id === 'super_admin' && <Shield size={12} aria-hidden="true" />}
      {text}
    </span>
  );
}

function PermissionTags({ permissions, limit }: { permissions: string[]; limit?: number }) {
  const visible = limit ? permissions.slice(0, limit) : permissions;
  const hidden = limit && permissions.length > limit ? permissions.length - limit : 0;

  return (
    <div className="wfx-admin-permission-tags">
      {visible.map((perm) => (
        <span key={perm} className="chip-pill wfx-admin-perm-chip" title={perm}>
          {perm.replace('.', ' · ')}
        </span>
      ))}
      {hidden > 0 && <span className="wfx-muted wfx-admin-perm-more">+{hidden} more</span>}
    </div>
  );
}

function RoleCard({
  role,
  onSelect,
}: {
  role: AdminRoleRow;
  onSelect: () => void;
}) {
  return (
    <button type="button" className="wfx-admin-role-card glass-surface" onClick={onSelect}>
      <div className="wfx-admin-role-card-head">
        <RoleBadge role={role} />
        <span className={`wfx-admin-tier-badge tier-${role.tier}`}>
          {role.tier === 'staff' ? 'Staff' : 'Student'}
        </span>
      </div>
      <p className="wfx-muted wfx-admin-role-card-desc">{role.description}</p>
      <div className="wfx-admin-role-card-stats">
        <span><Users size={14} aria-hidden="true" /> {role.userCount} users</span>
        <span><KeyRound size={14} aria-hidden="true" /> {role.permissionCount} permissions</span>
      </div>
      <PermissionTags permissions={role.permissions as string[]} limit={4} />
    </button>
  );
}

function RoleDetailDrawer({
  roleId,
  onClose,
}: {
  roleId: string | null;
  onClose: () => void;
}) {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin-role', roleId],
    queryFn: () => fetchAdminRole(roleId!),
    enabled: Boolean(roleId),
  });

  if (!roleId) return null;

  const role = data?.role;
  const recentUsers = data?.recentUsers ?? [];

  return (
    <AdminDetailCard
      open={Boolean(roleId)}
      onClose={onClose}
      ariaLabel="Role details"
      breadcrumb="Role definition"
      title={role?.label ?? 'Loading…'}
      illustration={role ? adminRoleIllustration(role.id) : 'admin.module.users'}
      subtitle={role ? <RoleBadge role={role} /> : undefined}
    >
      {isLoading && <LoadingState label="Loading role…" />}
      {isError && (
        <ErrorState message="Could not load role details." onRetry={() => void refetch()} />
      )}

      {role && (
        <>
          <p className="wfx-muted">{role.description}</p>

          <dl className="wfx-admin-meta-list wfx-admin-meta-list--grid">
            <div><dt>Tier</dt><dd className="capitalize">{role.tier}</dd></div>
            <div><dt>Rank</dt><dd>{role.rank}</dd></div>
            <div><dt>Users assigned</dt><dd>{role.userCount}</dd></div>
            <div><dt>Permissions</dt><dd>{role.permissionCount}</dd></div>
            <div><dt>Admin console</dt><dd>{role.isAdminRole ? 'Yes' : 'No'}</dd></div>
          </dl>

          <div className="wfx-admin-drawer-columns">
            <div className="wfx-admin-drawer-section">
              <h3>Permission groups</h3>
              <div className="wfx-admin-permission-groups">
                {role.permissionGroups.map((group) => (
                  <article key={group.group} className="wfx-admin-permission-group glass-surface">
                    <h4>{group.group}</h4>
                    <div className="wfx-admin-permission-tags">
                      {group.permissions.map((perm) => (
                        <span key={perm.id} className="chip-pill wfx-admin-perm-chip" title={perm.id}>
                          {perm.label}
                        </span>
                      ))}
                    </div>
                  </article>
                ))}
                {role.permissionGroups.length === 0 && (
                  <AdminEmptyState message="This role has no admin permissions." />
                )}
              </div>
            </div>

            <div className="wfx-admin-drawer-section">
              <div className="wfx-admin-drawer-section-head">
                <h3>Recent users</h3>
                <Link to={`/admin/users?role=${role.id}`} className="secondary-action compact">
                  View all
                  <ChevronRight size={14} aria-hidden="true" />
                </Link>
              </div>
              {recentUsers.length > 0 ? (
                <table className="wfx-admin-table wfx-admin-table--compact">
                  <thead>
                    <tr><th>Name</th><th>Status</th></tr>
                  </thead>
                  <tbody>
                    {recentUsers.map((user) => (
                      <tr key={user.id}>
                        <td>
                          <strong>{user.name}</strong>
                          <span className="wfx-muted wfx-admin-subline">{user.email ?? user.phone ?? '—'}</span>
                        </td>
                        <td><AdminStatusBadge status={user.status ?? 'active'} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <AdminEmptyState message="No users assigned to this role yet." />
              )}
            </div>
          </div>
        </>
      )}
    </AdminDetailCard>
  );
}

export function AdminRolesScreen() {
  const [query, setQuery] = useState('');
  const [tierFilter, setTierFilter] = useState('all');
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin-roles'],
    queryFn: fetchAdminRoles,
  });

  const roles = data?.roles ?? [];
  const stats = data?.stats;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return roles.filter((role) => {
      if (tierFilter !== 'all' && role.tier !== tierFilter) return false;
      if (!q) return true;
      return (
        role.label.toLowerCase().includes(q)
        || role.id.toLowerCase().includes(q)
        || role.description.toLowerCase().includes(q)
        || role.permissions.some((perm) => perm.toLowerCase().includes(q))
      );
    });
  }, [roles, query, tierFilter]);

  const kpiItems = useMemo(
    () => [
      { label: 'Roles', value: stats?.totalRoles ?? roles.length },
      { label: 'Staff roles', value: stats?.staffRoles ?? 0 },
      { label: 'Policies', value: stats?.permissionPolicies ?? 0 },
      { label: 'Staff users', value: stats?.staffUsers ?? 0 },
    ],
    [stats, roles.length],
  );

  if (isLoading && !data) return <LoadingState label="Loading roles…" />;
  if (isError || !data) return <ErrorState message="Could not load roles." onRetry={() => void refetch()} />;

  return (
    <>
      <AdminPageLayout
        breadcrumb="Admin / User Management / Roles"
        title="Roles"
        description="Role templates, access tiers, and permission assignments across the platform."
        icon={Shield}
        illustration="admin.module.users"
      >
        <AdminKpiStrip items={kpiItems} />

        <div className="wfx-admin-role-grid">
          {filtered.map((role) => (
            <RoleCard key={role.id} role={role} onSelect={() => setSelectedRoleId(role.id)} />
          ))}
        </div>

        <AdminTablePanel
          title="Role directory"
          toolbar={(
            <div className="wfx-admin-toolbar">
              <AdminSearchInput
                value={query}
                onChange={setQuery}
                placeholder="Search roles or permissions…"
              />
              <select
                className="wfx-admin-select"
                value={tierFilter}
                onChange={(e) => setTierFilter(e.target.value)}
                aria-label="Filter by tier"
              >
                <option value="all">All tiers</option>
                <option value="staff">Staff</option>
                <option value="student">Student</option>
              </select>
            </div>
          )}
        >
          <table className="wfx-admin-table">
            <thead>
              <tr>
                <th>Role</th>
                <th>Tier</th>
                <th>Users</th>
                <th>Permissions</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((role) => (
                <tr
                  key={role.id}
                  className="wfx-admin-table-row-clickable"
                  onClick={() => setSelectedRoleId(role.id)}
                >
                  <td>
                    <strong>{role.label}</strong>
                    <span className="wfx-muted wfx-admin-subline">{role.description}</span>
                  </td>
                  <td><span className={`wfx-admin-tier-badge tier-${role.tier}`}>{role.tier}</span></td>
                  <td>{role.userCount}</td>
                  <td><PermissionTags permissions={role.permissions as string[]} limit={3} /></td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <Link to={`/admin/users?role=${role.id}`} className="secondary-action compact">
                      View users
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <AdminEmptyState message="No roles match your filters." />}
        </AdminTablePanel>
      </AdminPageLayout>

      <RoleDetailDrawer
        roleId={selectedRoleId}
        onClose={() => setSelectedRoleId(null)}
      />
    </>
  );
}

export function AdminPermissionsScreen() {
  const [query, setQuery] = useState('');
  const [groupFilter, setGroupFilter] = useState('all');
  const [view, setView] = useState<'matrix' | 'groups' | 'list'>('matrix');
  const [selectedPermissionId, setSelectedPermissionId] = useState<string | null>(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin-permissions'],
    queryFn: fetchAdminPermissions,
  });

  const { data: detail } = useQuery({
    queryKey: ['admin-permission', selectedPermissionId],
    queryFn: () => fetchAdminPermission(selectedPermissionId!),
    enabled: Boolean(selectedPermissionId),
  });

  const permissions = data?.data ?? [];
  const groups = data?.groups ?? [];
  const matrixRoles = data?.matrixRoles ?? [];
  const stats = data?.stats;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return permissions.filter((item) => {
      if (groupFilter !== 'all' && item.group !== groupFilter) return false;
      if (!q) return true;
      return (
        item.label.toLowerCase().includes(q)
        || item.permission.toLowerCase().includes(q)
        || item.group.toLowerCase().includes(q)
        || item.roles.some((role) => role.toLowerCase().includes(q))
      );
    });
  }, [permissions, query, groupFilter]);

  const kpiItems = useMemo(
    () => [
      { label: 'Policies', value: stats?.total ?? permissions.length },
      { label: 'Groups', value: stats?.groupCount ?? groups.length },
      { label: 'Super-admin only', value: stats?.superAdminOnly ?? 0 },
      { label: 'Roles mapped', value: stats?.roleCount ?? matrixRoles.length },
    ],
    [stats, permissions.length, groups.length, matrixRoles.length],
  );

  if (isLoading && !data) return <LoadingState label="Loading permissions…" />;
  if (isError || !data) return <ErrorState message="Could not load permissions." onRetry={() => void refetch()} />;

  return (
    <>
      <AdminPageLayout
        breadcrumb="Admin / User Management / Permissions"
        title="Permissions"
        description="Fine-grained access policies mapped to roles across the admin console."
        icon={KeyRound}
        illustration="admin.module.users"
        actions={(
          <div className="wfx-admin-view-toggle" role="tablist" aria-label="Permission view">
            {(['matrix', 'groups', 'list'] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                role="tab"
                aria-selected={view === mode}
                className={`chip-pill${view === mode ? ' is-active' : ''}`}
                onClick={() => setView(mode)}
              >
                {mode === 'matrix' ? 'Matrix' : mode === 'groups' ? 'Groups' : 'List'}
              </button>
            ))}
          </div>
        )}
      >
        <AdminKpiStrip items={kpiItems} />

        <AdminTablePanel
          title={view === 'matrix' ? 'Access matrix' : view === 'groups' ? 'Permission groups' : 'All policies'}
          toolbar={(
            <div className="wfx-admin-toolbar">
              <AdminSearchInput
                value={query}
                onChange={setQuery}
                placeholder="Search permissions or roles…"
              />
              <select
                className="wfx-admin-select"
                value={groupFilter}
                onChange={(e) => setGroupFilter(e.target.value)}
                aria-label="Filter by group"
              >
                <option value="all">All groups</option>
                {groups.map((group) => (
                  <option key={group.group} value={group.group}>{group.group}</option>
                ))}
              </select>
            </div>
          )}
        >
          {view === 'matrix' && (
            <table className="wfx-admin-table wfx-admin-matrix-table">
              <thead>
                <tr>
                  <th>Permission</th>
                  {matrixRoles.map((role) => (
                    <th key={role.id} className="wfx-admin-matrix-role-col">{role.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((item) => (
                  <tr
                    key={item.id}
                    className="wfx-admin-table-row-clickable"
                    onClick={() => setSelectedPermissionId(item.id)}
                  >
                    <td>
                      <strong>{item.label}</strong>
                      <span className="wfx-muted wfx-admin-subline">{item.group}</span>
                    </td>
                    {matrixRoles.map((role) => (
                      <td key={role.id} className="wfx-admin-matrix-cell">
                        {item.roles.includes(role.id) ? (
                          <span className="wfx-admin-matrix-check" aria-label={`Allowed for ${role.label}`}>✓</span>
                        ) : (
                          <span className="wfx-admin-matrix-dash" aria-hidden="true">—</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {view === 'groups' && (
            <div className="wfx-admin-permission-groups">
              {groups
                .filter((group) => groupFilter === 'all' || group.group === groupFilter)
                .map((group) => {
                  const items = group.permissions.filter((item) => filtered.some((f) => f.id === item.id));
                  if (items.length === 0) return null;
                  return (
                    <article key={group.group} className="wfx-admin-permission-group glass-surface">
                      <header className="wfx-admin-permission-group-head">
                        <h3>{group.group}</h3>
                        <span className="wfx-muted">{items.length} policies</span>
                      </header>
                      <div className="wfx-admin-permission-list">
                        {items.map((item) => (
                          <button
                            key={item.id}
                            type="button"
                            className="wfx-admin-permission-card"
                            onClick={() => setSelectedPermissionId(item.id)}
                          >
                            <strong>{item.label}</strong>
                            <code>{item.permission}</code>
                            <div className="wfx-admin-permission-tags">
                              {item.roles.map((role) => (
                                <span key={role} className={`wfx-admin-role-badge role-${role}`}>
                                  {role.replace('_', ' ')}
                                </span>
                              ))}
                            </div>
                          </button>
                        ))}
                      </div>
                    </article>
                  );
                })}
            </div>
          )}

          {view === 'list' && (
            <table className="wfx-admin-table">
              <thead>
                <tr><th>Permission</th><th>Group</th><th>Roles</th><th>Scope</th></tr>
              </thead>
              <tbody>
                {filtered.map((item) => (
                  <tr
                    key={item.id}
                    className="wfx-admin-table-row-clickable"
                    onClick={() => setSelectedPermissionId(item.id)}
                  >
                    <td>
                      <strong>{item.label}</strong>
                      <span className="wfx-muted wfx-admin-subline"><code>{item.permission}</code></span>
                    </td>
                    <td>{item.group}</td>
                    <td>
                      <div className="wfx-admin-permission-tags">
                        {item.roles.map((role) => (
                          <span key={role} className={`wfx-admin-role-badge role-${role}`}>
                            {role.replace('_', ' ')}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>
                      {item.superAdminOnly ? (
                        <span className="wfx-admin-tier-badge tier-staff">Super admin</span>
                      ) : (
                        <span className="wfx-muted">{item.roleCount} roles</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {filtered.length === 0 && <AdminEmptyState message="No permissions match your filters." />}
        </AdminTablePanel>
      </AdminPageLayout>

      {selectedPermissionId && (
        <AdminDetailCard
          open={Boolean(selectedPermissionId)}
          onClose={() => setSelectedPermissionId(null)}
          ariaLabel="Permission details"
          breadcrumb="Permission policy"
          title={detail?.permission.label ?? selectedPermissionId}
          illustration={adminDetailIllustration('permission')}
          subtitle={
            detail?.permission ? (
              <code className="wfx-admin-subline">{detail.permission.permission}</code>
            ) : undefined
          }
        >
          {detail && (
            <>
              <dl className="wfx-admin-meta-list wfx-admin-meta-list--grid">
                <div><dt>Group</dt><dd>{detail.permission.group}</dd></div>
                <div><dt>Roles granted</dt><dd>{detail.permission.roleCount}</dd></div>
                <div><dt>Scope</dt><dd>{detail.permission.superAdminOnly ? 'Super admin only' : 'Shared access'}</dd></div>
              </dl>

              <div className="wfx-admin-drawer-columns">
                <div className="wfx-admin-drawer-section">
                  <h3>Allowed roles</h3>
                  <div className="wfx-admin-permission-tags">
                    {detail.roles.map((role) => (
                      <Link key={role.id} to="/admin/roles" className={`wfx-admin-role-badge role-${role.id}`}>
                        {role.label} ({role.userCount})
                      </Link>
                    ))}
                  </div>
                </div>

                <div className="wfx-admin-drawer-section">
                  <div className="wfx-admin-drawer-section-head">
                    <h3>Users with access</h3>
                    <Link to="/admin/users" className="secondary-action compact">
                      View users
                      <ChevronRight size={14} aria-hidden="true" />
                    </Link>
                  </div>
                  {detail.recentUsers.length > 0 ? (
                    <table className="wfx-admin-table wfx-admin-table--compact">
                      <thead>
                        <tr><th>Name</th><th>Role</th></tr>
                      </thead>
                      <tbody>
                        {detail.recentUsers.map((user) => (
                          <tr key={user.id}>
                            <td>
                              <strong>{user.name}</strong>
                              <span className="wfx-muted wfx-admin-subline">{user.email ?? user.phone ?? '—'}</span>
                            </td>
                            <td><span className={`wfx-admin-role-badge role-${user.role}`}>{user.role.replace('_', ' ')}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <AdminEmptyState message="No users currently have this permission." />
                  )}
                </div>
              </div>
            </>
          )}
        </AdminDetailCard>
      )}
    </>
  );
}
